import { ExternalLinkIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { fileUrl, isImage, isPdf, type AttachmentView } from './api'

/**
 * Show an attachment without leaving the page.
 *
 * PDFs go in an iframe against the same origin, so the session cookie is sent
 * and the browser's own viewer does the work. Anything the browser cannot render
 * gets a download link rather than a broken frame.
 */
export function AttachmentViewer({
  attachment,
  onClose,
}: {
  attachment: AttachmentView | null
  onClose: () => void
}) {
  if (!attachment) return null

  const href = fileUrl(attachment)
  const external = Boolean(attachment.external_url)

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[85vh] max-w-5xl flex-col">
        <DialogHeader>
          <DialogTitle className="truncate pr-8">{attachment.name}</DialogTitle>
        </DialogHeader>

        <div className="bg-muted/30 min-h-0 flex-1 overflow-auto rounded-md">
          {external ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
              <p className="text-muted-foreground text-sm">
                This is a link. Fundus has no copy of the file.
              </p>
              <Button asChild>
                <a href={href} target="_blank" rel="noreferrer noopener">
                  <ExternalLinkIcon />
                  Open it
                </a>
              </Button>
            </div>
          ) : isPdf(attachment) ? (
            <iframe src={href} title={attachment.name} className="h-full w-full border-0" />
          ) : isImage(attachment) ? (
            <div className="flex h-full items-center justify-center p-4">
              <img
                src={href}
                alt={attachment.name}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
              <p className="text-muted-foreground text-sm">This kind of file has no preview.</p>
              <Button asChild>
                <a href={href} target="_blank" rel="noreferrer noopener">
                  Download it
                </a>
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
